import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type VendorTeamDepartureJSON = z.infer<typeof VendorTeamDepartureSchema.VendorTeamDepartureJSONZ>;
export type VendorTeamDepartureParams = z.infer<typeof VendorTeamDepartureSchema.VendorTeamDepartureZ>;

/**
 * Why a person is no longer on a work agreement's vendor team (#5663).
 *
 * The values are the names the API sends, which are the names of the server's own
 * `VendorTeamDepartureReason`. A payload that carries a reason this build does not know is read as
 * `Removed`: the roster still has to say that the person is gone, and "somebody took them off" is the
 * weaker of the two claims.
 */
export enum VendorTeamDepartureReason {
  /** The member ended their own participation in the work. */
  Left = "Left",
  /** Somebody other than the member ended their participation in the work. */
  Removed = "Removed",
}

export default class VendorTeamDepartureSchema {
  public static readonly VendorTeamDepartureJSONZ = z.object({
    userId: z.uuid(),
    legalEntityId: z.uuid(),
    reason: z.string(),
    departedDate: z.iso.datetime({ offset: true }),
  });

  public static readonly VendorTeamDepartureZ = z.object({
    userId: z.instanceof(Guid),
    legalEntityId: z.instanceof(Guid),
    reason: z.enum(VendorTeamDepartureReason),
    departedDate: DateSchema.type,
  });

  public static validate(values: unknown): VendorTeamDepartureParams {
    return parseOrThrow(VendorTeamDepartureSchema.VendorTeamDepartureZ, values, {
      logError: true,
      schemaName: "VendorTeamDepartureSchema",
      operation: "Validating VendorTeamDepartureParams",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): VendorTeamDepartureJSON {
    return parseOrThrow(VendorTeamDepartureSchema.VendorTeamDepartureJSONZ, json, {
      logError: true,
      schemaName: "VendorTeamDepartureSchema",
      operation: "Validating VendorTeamDepartureJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(values: unknown): values is VendorTeamDepartureJSON {
    try {
      VendorTeamDepartureSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: VendorTeamDepartureJSON): VendorTeamDepartureParams {
    const parsed = VendorTeamDepartureSchema.validateJSON(data);
    return {
      userId: Guid.fromJSON(parsed.userId)!,
      legalEntityId: new Guid(parsed.legalEntityId, { allowEmpty: true }),
      reason: VendorTeamDepartureSchema.reasonFromJSON(parsed.reason),
      departedDate: AHDate.fromJSON(parsed.departedDate)!,
    };
  }

  public static serialize(departure: VendorTeamDepartureParams): VendorTeamDepartureJSON {
    const parsed = VendorTeamDepartureSchema.validate(departure);
    return {
      userId: parsed.userId.toJSON(),
      legalEntityId: parsed.legalEntityId.toJSON(),
      reason: parsed.reason,
      departedDate: parsed.departedDate.toJSON(),
    };
  }

  private static reasonFromJSON(reason: string | null | undefined): VendorTeamDepartureReason {
    if (isNullOrUndefined(reason)) return VendorTeamDepartureReason.Removed;
    return reason === VendorTeamDepartureReason.Left
      ? VendorTeamDepartureReason.Left
      : VendorTeamDepartureReason.Removed;
  }
}
